import React from 'react';
import { motion } from 'framer-motion';

const ExampleCases = () => {
  const cases = [
    {
      icon: "✈️",
      title: "Unmanned Vehicles and Robotics",
      description: "",
      features: [
        "Simulates gyroscope, accelerometer and all the relevant data for complex patterns",
        "Generates rare conditions like sudden turbulence or system malfunctions",
        "Creates varied terrain and weather scenarios in a single labeled dataset"
      ]
    },
    {
      icon: "🔧",
      title: "Environmental Monitoring and Predictive Maintenance",
      description: "",
      features: [
        "Models diverse environmental conditions for smart sensors",
        "Simulates rare events for safety and precise measurement systems",
        "Creates comprehensive failure mode data without damaging real equipment",
        "Simulates sensor readings for cascading failures and hazardous conditions",
        "Builds datasets spanning years of wear patterns in a fraction of the time"
      ]
    },
    {
      icon: "🏥",
      title: "Healthcare and Life Sciences",
      description: "Simulates realistic patient vital signs, diagnostics, and treatment response time series — all generated synthetically, without using any personal data. Ideal for developing and validating AI models in medical research, clinical analytics, and device testing while maintaining full privacy compliance.",
      features: [
        "Supports training for early warning systems and digital twin simulations",
        "Privacy-first: Fully synthetic and statistically representative — compliant with HIPAA, GDPR, and all major healthcare data standards"
      ]
    }
  ];

  return (
    <section id="examples" className="section relative overflow-hidden bg-background-secondary">
      {/* Background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-gradient-to-r from-secondary/5 to-accent/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-gradient-to-r from-primary/5 to-secondary/5 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
          className="text-center mb-16"
        >
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Use <span className="bg-gradient-to-r from-secondary to-accent bg-clip-text text-transparent">Cases</span>
          </h2>
          <p className="text-lg text-foreground-muted max-w-3xl mx-auto leading-relaxed">
            Prepare comprehensive datasets for diverse applications, including rare and dangerous scenarios. Pre-trained AI agents imagine different equipment-related scenarios and generate consistent, well-labeled synthetic data. This approach captures edge cases that would be nearly impossible to obtain in real-world testing.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {cases.map((caseItem, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1, ease: [0.4, 0, 0.2, 1] }}
            >
              <div className="card-elevated h-full flex flex-col group">
                <div className="relative z-10 px-8 py-12 flex flex-col h-full">
                  <div className="mb-6">
                    <h3 className="text-xl font-bold mb-4 text-foreground group-hover:text-secondary transition-colors">
                      {caseItem.title}
                    </h3>
                    
                    <p className="text-foreground-muted leading-relaxed mb-6">
                      {caseItem.description}
                    </p>
                    
                    <div className="mb-6">
                      <ul className="space-y-3">
                        {caseItem.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-start">
                            <div className="w-5 h-5 bg-accent/20 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                              <svg className="w-3 h-3 text-accent" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <span className="text-foreground-muted text-sm leading-relaxed">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  
                  {/* Removed per-card consultant button as requested */}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4, ease: [0.4, 0, 0.2, 1] }}
          className="text-center mt-16"
        >
          <p className="text-sm text-foreground-secondary max-w-3xl mx-auto leading-relaxed font-mono">
            <span className="text-foreground-secondary mr-3">[INFO]</span>
            <span className="ml-3">For dangerous scenarios, synthetic datasets should be used only for improving real data collection</span>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default ExampleCases;
